/**
 * collect 페이지 문자열 (TASK-2026-004 SPEC 「포함 범위」 2·3·6).
 *
 * 화면에 문구를 흩지 않는다. 동의·오류·저장 상태 문구는 참가자가 사실을 오해하지 않도록
 * 정확히 써야 하고, 그러려면 한자리에 모여 있어야 한다.
 */

export const collectKo = {
  title: 'Color Cube Atlas 체험',
  loading: '불러오는 중...',

  campaign: {
    closedTitle: '지금은 접수가 닫혀 있습니다',
    closed: '이 체험은 현재 새 참여를 받지 않습니다. 안내받은 담당자에게 문의해 주세요.',
    notFound: '이 주소의 체험을 찾을 수 없습니다. 받은 링크를 다시 확인해 주세요.',
    unreachable: '체험 정보를 불러오지 못했습니다. 잠시 뒤 다시 시도해 주세요.',
  },

  consent: {
    heading: '무엇을 수집하고 어떻게 쓰는지 먼저 읽어 주세요',
    agree: '위 내용을 읽고 참여에 동의합니다',
    start: '동의하고 시작하기',
    decline: '참여하지 않기',
    declined: '참여하지 않기를 선택했습니다. 아무 기록도 보내지 않았습니다.',
    /** 동의 전에는 원격 계측이 0이다. 참가자에게도 그 사실을 말해 준다. */
    beforeConsent: '동의하기 전에는 어떤 행동도 서버로 보내지 않습니다.',
    required: '동의에 체크해야 시작할 수 있습니다.',
    versionChanged: '안내 내용이 바뀌었습니다. 새로 읽고 다시 동의해 주세요.',
  },

  invite: {
    heading: '초대코드를 입력해 주세요',
    help: '안내받은 1회용 코드입니다. 이름이나 연락처는 묻지 않습니다.',
    label: '초대코드',
    submit: '참여 시작',
    invalid: '이 코드로는 시작할 수 없습니다. 코드를 다시 확인해 주세요.',
    full: '모집 인원이 모두 찼습니다.',
    dailyLimit: '오늘 받을 수 있는 인원을 넘었습니다. 내일 다시 시도해 주세요.',
    limited: '요청이 잠시 제한되었습니다. 1분 뒤에 다시 시도해 주세요.',
    failed: '참여를 시작하지 못했습니다. 잠시 뒤 다시 시도해 주세요.',
  },

  /** 세션 생성 결과를 확정하지 못했을 때 (TASK-2026-011 CR-01 §4). 거부로 바꾸지 않는다. */
  start: {
    pendingTitle: '참여 시작 결과를 확인하고 있습니다',
    pending:
      '서버 응답을 받지 못해 참여가 시작됐는지 아직 확인하지 못했습니다. 새로 시작하지 말고 잠시 뒤 결과를 다시 확인해 주세요. 같은 요청의 결과를 확인하므로 참여가 두 번 생기지 않습니다.',
    recheck: '결과 다시 확인',
    expired: '참여 시작 요청의 유효 시간이 지났습니다. 처음부터 다시 시작해 주세요.',
    activeElsewhere: '이 브라우저에 진행 중인 다른 체험이 있습니다. 그 체험을 마치거나 철회한 뒤 다시 시작해 주세요.',
  },

  q1: {
    heading: '게임을 시작하기 전에 한 가지만 묻겠습니다',
    dontKnow: '모르겠다 / 응답하지 않음',
    /** 빈 답이 곧 '모르겠다 / 응답하지 않음'이라는 것을 알린다 (TASK-2026-014). */
    emptyHint: "비워 두면 '모르겠다 / 응답하지 않음'으로 저장됩니다.",
    submit: '저장하고 게임 시작',
    /** Q1을 플레이 뒤의 답으로 덮지 않는다는 사실을 참가자에게도 알린다. */
    locked: '이 답은 이미 저장되었습니다. 플레이 뒤에 고치지 않습니다.',
  },

  play: {
    heading: '게임을 실행합니다',
    start: '게임 시작',
    resume: '게임으로 돌아가기',
    survey: '체험 종료 / 의견 남기기',
    note: '게임 중 언제든 체험을 끝내고 의견을 남길 수 있습니다. 끝까지 하지 않아도 됩니다.',
  },

  survey: {
    heading: '체험은 어땠나요?',
    note: '이름·연락처는 적지 마세요. 모르는 것은 "모르겠다 / 응답하지 않음"을 골라도 됩니다.',
    dontKnow: '모르겠다 / 응답하지 않음',
    clear: '이 답 지우기',
    submit: '의견 제출하고 마치기',
    saveDraft: '임시 저장',
    unanswered: (count: number) => `응답하지 않은 문항이 ${String(count)}개 있습니다. 그대로 제출해도 됩니다.`,
    chars: (used: number, max: number) => `${String(used)} / ${String(max)}자`,
    tooLong: '글자 수 상한을 넘었습니다. 조금 줄여 주세요.',
    contactLike: '연락처처럼 보이는 내용이 있습니다. 개인정보는 저장하지 않으니 지워 주세요.',
    invalid: '고를 수 없는 값이 들어갔습니다. 다시 골라 주세요.',
  },

  /** 링크와 저장된 참여가 어긋났을 때 (REV-008). */
  mismatch: {
    campaignTitle: '다른 체험의 참여 기록이 있습니다',
    campaign:
      '지금 링크의 체험과 이 브라우저에 저장된 참여 기록의 체험이 다릅니다. 두 체험의 자료가 섞이지 않도록 진행과 전송을 멈췄습니다.',
    sessionTitle: '다른 참여 기록과 연결되어 있습니다',
    session:
      '이 브라우저에 저장된 참여와 서버가 알려 준 참여가 다릅니다. 자료가 다른 참여에 섞이지 않도록 진행과 전송을 멈췄습니다.',
    consentTitle: '안내 내용이 바뀌었습니다',
    consent: '저장된 참여는 이전 안내 내용에 동의한 것입니다. 바뀐 안내를 읽고 다시 동의해야 이어갈 수 있습니다.',
    /** 같은 코호트의 다른 모집 링크로 이미 참여했다 (TASK-2026-011 SPEC §5). */
    cohortTitle: '이미 이 체험에 참여했습니다',
    cohort: '같은 체험의 다른 모집 링크로 이미 참여했습니다. 처음 참여한 링크로 이어서 진행해 주세요.',
    resume: '저장된 체험으로 이어가기',
    fresh: '이 체험으로 새로 시작하기',
    reconsent: '새로 읽고 다시 동의하기',
  },

  /** 서버가 확정한 단일 결과 문서 (REV-007). */
  result: {
    heading: '확정된 결과',
    note: '서버가 확정한 결과 문서 한 개입니다. 아래 내용이 서버에 저장된 것과 같은 문서입니다.',
    show: '결과 JSON 보기',
    download: '결과 JSON 내려받기',
    digest: '결과 지문',
    unverified: '서버가 확정한 결과 문서를 아직 확인하지 못했습니다. 확인하기 전에는 저장 완료로 표시하지 않습니다.',
    recheck: '결과 다시 확인',
  },

  status: {
    heading: '저장 상태',
    sending: '전송 중',
    pending: '일부 미전송',
    saved: '저장 완료',
    /**
     * 서버는 확정했지만 이 세션의 저장 확인 기록 일부를 기기에서 잃었다 (R2-REV-005/006).
     * 확정 결과 문서에는 이 손실이 적히지 않으므로 완료로 덮지 않는다.
     */
    savedWithLoss: (dropped: number) => `서버 확정 완료 — 단, 저장 확인 기록 ${String(dropped)}건을 이 기기에서 잃었습니다`,
    /**
     * 저장 확인 기록 spool을 확인할 수 없다 (CR-01 A+ §2.5). 손실이 있었는지조차 말할 수 없으므로
     * 완료로도 손실로도 부르지 않는다.
     */
    savedUnverified: '저장 상태 확인 불가',
    unverifiedNote:
      '의견과 기록은 서버에 보냈지만, 이 기기에서 저장 확인 기록을 끝까지 확인하지 못했습니다. 아래 저장 상태에서 복구 파일을 내려받아 담당자에게 전달해 주세요.',
    lossNote:
      '의견과 기록은 서버가 확정했습니다. 다만 이 기기의 저장 확인 기록 일부가 상한을 넘어 버려졌고, 이 손실은 확정 결과 문서에 적히지 않습니다. 복구 파일을 내려받아 담당자에게 전달해 주세요.',
    /** 아직 최종 제출 전이다. 보낼 것이 없다는 사실을 완료로 바꾸지 않는다(REV-006). */
    beforeSubmit: '아직 제출하지 않았습니다 — 여기까지 서버가 받았습니다',
    /** 제출은 했지만 확정 결과를 아직 확인하지 못했다(REV-006). */
    confirming: '서버 확인 중',
    offline: '오프라인 — 연결되면 이어서 보냅니다',
    readonly: '다른 탭에서 진행 중입니다. 이 탭은 보기 전용입니다',
    blocked: '더 보낼 수 없습니다',
    retry: '지금 다시 보내기',
    /** 완료는 서버가 마지막 응답·batch를 확인한 뒤에만 표시한다(F1-04.8). */
    savedNote: '서버가 마지막 기록과 의견을 모두 확인했습니다. 창을 닫아도 됩니다.',
    pendingNote: '아직 서버가 받지 못한 기록이 있습니다. 이 창을 열어 두면 계속 보냅니다.',
    detail: (through: number, local: number) => `서버 확인 ${String(through)} / 기록 ${String(local)}`,
    /** 아직 서버 ACK를 받지 못한 저장 확인 기록과 버려진 기록 수(REV-005). */
    confirmationDetail: (pending: number, dropped: number) =>
      `저장 확인 기록 미전송 ${String(pending)}건 / 버려짐 ${String(dropped)}건`,
    incomplete: '서버가 아직 완료로 확정하지 않았습니다. 남은 기록을 보낸 뒤 다시 시도합니다.',
    recovery: '복구 파일 내려받기',
    recoveryNote: '연결이 계속 안 되면 이 파일을 담당자에게 전달해 주세요. 정상 경로는 온라인 전송입니다.',
    withdraw: '동의 철회하고 수집 중지',
    withdrawn: '동의를 철회했습니다. 이후 수집을 멈췄습니다.',
  },

  /** 서버 연결 점검 (TASK-2026-012 CR-01). 브라우저 표시가 아니라 실제 점검 결과다. */
  connection: {
    unknown: '서버 연결 확인 전',
    checking: '서버 연결 확인 중',
    connected: '서버 연결됨',
    disconnected: '서버 연결 안 됨',
    check: '온라인 상태 점검',
    limited:
      '서버에 연결되지 않아 제출·저장처럼 서버가 필요한 동작을 잠시 막았습니다. 게임 기록은 이 기기에 남아 있고, 연결되면 이어서 보냅니다.',
  },

  /** 제출했지만 `/complete`를 보내지 않았다 (CR-01 §6). 의견 저장과 기록 전송을 구분해 말한다. */
  submitHeld: {
    unsynced:
      '의견은 서버에 저장했지만 플레이 기록이 아직 모두 전달되지 않아 제출을 마치지 못했습니다. 연결을 확인한 뒤 다시 제출해 주세요.',
    readonly: '다른 탭에서 진행 중이라 이 탭에서는 제출을 마치지 않았습니다.',
    blocked: (code: string) => `기록을 더 보낼 수 없어 제출을 마치지 못했습니다 (${code}). 복구 파일을 내려받아 담당자에게 전달해 주세요.`,
    /**
     * 이 참여의 최종 답변이 이미 서버에 잠겼는데, 그것이 이 화면의 답과 같다고 확인할 수 없다
     * (REV-003). 다른 탭의 답을 이 화면의 제출로 확정하지 않는다.
     */
    conflict: (reason: string) =>
      `이 참여의 최종 답변이 이미 다른 곳(다른 탭 등)에서 저장되어, 이 화면의 답변으로는 제출을 마치지 않았습니다 (${reason}). 복구 파일을 내려받아 담당자에게 전달해 주세요.`,
  },

  error: {
    sessionExpired: '재개 기간이 지났습니다. 새로 시작해야 합니다.',
    sessionWithdrawn: '동의를 철회한 세션입니다.',
    network: '연결이 되지 않습니다. 연결되면 자동으로 이어 보냅니다.',
    unexpected: '예상하지 못한 문제가 생겼습니다.',
  },
} as const;
